import { FC, useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@eon-home/react-library';

import { SpotPriceModel, SchedulingTimeModel } from '@swagger-http';

import { Prompt } from '@components';
import { WithErrorAndLoader } from '@hocs/with-error-and-loader';
import { Nullable, SpotPriceType } from '@tools/types';
import { Moment, isInvalidValue, LocalizationConfig } from '@tools/utils';
import { getIntervalPeriod, addIntervalToSpotPriceEntry } from './settings';
import {
    getLocale,
    getSpotPrice,
    getWallboxChargingModesData,
} from '@store/actions';
import {
    BillingRegion,
    SpotPriceGraph,
    SpotPriceSVGFilters,
} from '@containers/emobility';
import {
    useSpotPrice,
    useSiteTimezone,
    useBillingRegion,
    useShouldShowRegionSelector,
    useWallboxDynamicPriceChargingData,
    useWallboxConfigId,
} from '@store/selectors';

import Pin from '@assets/icons/misc/pin.svg';

import './index.scss';
import { processGroups } from './helpers';

export interface SpotPricePoint {
    x: string;
    y: number | null;
    z?: SchedulingTimeModel;
    isAdditional?: boolean;
}

interface SpotPriceProps {
    type?: SpotPriceType;
}

interface SpotPriceContentProps extends SpotPriceProps {
    data: Nullable<SpotPriceModel[]>;
}

export const SpotPriceEmpty: FC = () => {
    const { t } = useTranslation();

    return (
        <div className="c-spot-price__message">
            <p>{t('We do not seem to be receiving any data.')}</p>

            <p>{t('Please try again later.')}</p>
        </div>
    );
};

export const SpotPriceContent: FC<Readonly<SpotPriceContentProps>> = ({
    data,
    type,
}: SpotPriceContentProps) => {
    const { t } = useTranslation();
    const locale = getLocale();
    const dispatch = useDispatch();
    const timezone = useSiteTimezone();
    const { data: region } = useBillingRegion();
    const showRegionSelector = useShouldShowRegionSelector();
    const { schedulingTimes } = useWallboxDynamicPriceChargingData();
    const configurationId = useWallboxConfigId();

    const [active, setActive] = useState(0);
    const [showPrompt, setShowPrompt] = useState(false);

    const withTimezone = useCallback(
        (value?: string) => Moment(value).tz(timezone),
        [timezone],
    );

    const parts = useMemo(() => {
        if (!data || data.length === 0) {
            return {};
        }

        const groups = data.reduce(
            (
                result: Record<string, SpotPricePoint[]>,
                item: SpotPriceModel,
            ) => {
                const date = withTimezone(item.timestamp).format(
                    LocalizationConfig.dateTimePatterns[locale].graphMonth,
                );

                result[date] = result[date] ?? [];
                result[date].push({
                    x: item.timestamp,
                    y: isInvalidValue(item.price) ? null : item.price / 100,
                });

                return result;
            },
            {},
        );

        Object.keys(groups).forEach((key) => {
            groups[key].push(
                addIntervalToSpotPriceEntry(
                    groups[key].at(-1)!,
                    timezone,
                    getIntervalPeriod(groups[key]),
                ),
            );
        });

        processGroups(groups, timezone, schedulingTimes);

        return groups;
    }, [data, locale, timezone, withTimezone, schedulingTimes]);

    return (
        <>
            <div className="c-spot-price__dates">
                {Object.keys(parts).map((date: string, index: number) => (
                    <Button
                        key={index}
                        type="button"
                        small
                        onClick={() => setActive(index)}
                        className={
                            active === index
                                ? 'c-btn--turquoise'
                                : 'c-btn--grey'
                        }
                    >
                        {date}
                    </Button>
                ))}
            </div>

            <SpotPriceSVGFilters />

            {Object.values(parts).map(
                (items: SpotPricePoint[], index: number) =>
                    active !== index ? null : (
                        <SpotPriceGraph key={index} data={items} type={type} />
                    ),
            )}

            {showRegionSelector && (
                <div className="c-emobility__infobox">
                    <Icon
                        src={Pin}
                        className="c-spot-price__infobox-icon c-spot-price__infobox-icon--pin"
                    />

                    <p>
                        {t('Spot price region:')}
                        <br />
                        <strong>{region?.displayName}</strong>
                    </p>

                    <Button
                        small
                        type="button"
                        className="c-btn--turquoise"
                        onClick={() => setShowPrompt(true)}
                    >
                        {t('Change region')}
                    </Button>
                </div>
            )}

            {showPrompt && (
                <Prompt
                    small
                    onClose={() => {
                        setShowPrompt(!showPrompt);
                    }}
                    isOverlaying
                    className="c-spot-price__wrapper"
                >
                    <BillingRegion
                        force
                        onSelect={() => {
                            setShowPrompt(false);
                            dispatch(getSpotPrice());
                            dispatch(
                                getWallboxChargingModesData(
                                    configurationId || '',
                                    true,
                                ),
                            );
                        }}
                    />
                </Prompt>
            )}
        </>
    );
};

export const SpotPrice: FC<Readonly<SpotPriceProps>> = ({
    type,
}: SpotPriceProps) => {
    const dispatch = useDispatch();
    const {
        data: region,
        error: regionError,
        loading: regionLoading,
    } = useBillingRegion();
    const showRegionSelector = useShouldShowRegionSelector();
    const configurationId = useWallboxConfigId();
    const { data, error, loading } = useSpotPrice();
    const isNot404 = error !== null && error !== 404;

    const isEmpty = useMemo(() => {
        if (error !== null) {
            return false;
        }

        if (!data || data.length === 0) {
            return true;
        }

        return data.map((value) => value.price).filter(Boolean).length === 0;
    }, [data, error]);

    useEffect(() => {
        dispatch(getSpotPrice());
    }, [dispatch]);

    const renderComponent = useMemo(() => {
        if (isEmpty) {
            return <SpotPriceEmpty />;
        }

        if (!showRegionSelector || region) {
            return <SpotPriceContent data={data} type={type} />;
        }

        return (
            <BillingRegion
                onSelect={() => {
                    dispatch(getSpotPrice());
                    dispatch(
                        getWallboxChargingModesData(
                            configurationId || '',
                            true,
                        ),
                    );
                }}
            />
        );
    }, [
        type,
        data,
        region,
        isEmpty,
        dispatch,
        configurationId,
        showRegionSelector,
    ]);

    return (
        <div className="c-spot-price">
            <div className="c-spot-price__graph">
                <WithErrorAndLoader
                    error={isNot404 || regionError}
                    loading={loading || regionLoading}
                >
                    {renderComponent}
                </WithErrorAndLoader>
            </div>
        </div>
    );
};

export default SpotPrice;
